import type { FilterOption } from "./OptionsFilterDropdown";

/** Máximo de opções renderizadas por vez — listas grandes travam o DOM. */
export const OPTIONS_RENDER_LIMIT = 100;

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

export interface FilteredOptions {
  visible: FilterOption[];
  /** Quantas casaram com a busca, antes do corte. */
  total: number;
  /** Verdadeiro quando há mais resultados do que o limite exibido. */
  truncated: boolean;
}

/**
 * Busca sem acento e sem diferenciar maiúsculas, com corte de segurança.
 *
 * Os itens já selecionados vêm primeiro e não são cortados **enquanto
 * couberem no limite** — sem isso, uma seleção pequena fora das primeiras 100
 * sumiria da lista e não daria para desmarcar. Mas o limite em si nunca é
 * ultrapassado: "Selecionar todos" em milhares de opções deixa
 * `selected.length` bem maior que `limit`, e renderizar tudo mesmo assim é o
 * que travava a interação por alguns segundos a cada clique (milhares de
 * `CommandItem`/`Checkbox` remontando). Passado o limite, a busca é o jeito
 * de achar um selecionado específico pra desmarcar.
 */
export const filterOptions = (
  options: FilterOption[],
  term: string,
  selected: string[] = [],
  limit: number = OPTIONS_RENDER_LIMIT,
): FilteredOptions => {
  const needle = normalize(term);
  const matches = needle
    ? options.filter((option) => normalize(option.label).includes(needle))
    : options;

  const selectedSet = new Set(selected);
  const selectedMatches = matches.filter((option) =>
    selectedSet.has(option.value),
  );
  const rest = matches.filter((option) => !selectedSet.has(option.value));

  const visibleSelected = selectedMatches.slice(0, limit);
  const room = Math.max(limit - visibleSelected.length, 0);
  const visible = [...visibleSelected, ...rest.slice(0, room)];

  return {
    visible,
    total: matches.length,
    truncated: matches.length > visible.length,
  };
};
